import type {
  DatabaseImportColumnPayload,
  DatabaseImportRowPayload,
  DatabaseImportSourcePayload,
} from './database-import-client';

export type TabularImportColumn = {
  id: string;
  name: string;
  type: string;
  data?: Record<string, unknown>;
};

export type TabularImportSource = {
  name: string;
  columns: TabularImportColumn[];
  rows: Record<string, unknown>[];
};

export type DatabaseImportSourceMetadata = Omit<
  DatabaseImportSourcePayload,
  'rows'
> & {
  rowCount: number;
  width?: number;
  height?: number;
};

const DEFAULT_IMPORT_POSITION = { x: 400, y: 300 };
const DATABASE_X_OFFSET = 32;

export function titleColumnIndex(columns: { id: string }[], index = 0): number {
  if (columns.length === 0) return 0;
  return Math.min(Math.max(index, 0), columns.length - 1);
}

export function makeDatabaseImportRows(
  rows: Record<string, unknown>[],
  source: string
): DatabaseImportRowPayload[] {
  return rows.map((values, index) => ({
    id: `${source}-row-${index + 1}`,
    values,
  }));
}

export function importSourceMetadata(
  source: Omit<TabularImportSource, 'rows'> & { rowCount: number },
  index: number,
  options: {
    titleColumnIndex?: number;
    dropPosition?: { x: number; y: number };
    sourceFileName?: string;
    sourceSheetName?: string;
  }
): DatabaseImportSourceMetadata {
  const selectedTitleIndex = titleColumnIndex(
    source.columns,
    options.titleColumnIndex
  );
  const position = options.dropPosition ?? DEFAULT_IMPORT_POSITION;
  const columns: DatabaseImportColumnPayload[] = source.columns.map(
    (column, columnIndex) => ({
      id: column.id,
      name: column.name,
      type: columnIndex === selectedTitleIndex ? 'title' : column.type,
      ...(column.data ? { data: column.data } : {}),
    })
  );
  const titleColumnId = columns[selectedTitleIndex]?.id;
  if (!titleColumnId) {
    throw new Error(`Database import source "${source.name}" has no columns.`);
  }

  return {
    clientId: `${options.sourceFileName ?? source.name}:${options.sourceSheetName ?? source.name}:${index}`,
    title: source.name,
    x: position.x + index * DATABASE_X_OFFSET,
    y: position.y,
    columns,
    titleColumnId,
    rowCount: source.rowCount,
    sourceFileName: options.sourceFileName,
    sourceSheetName: options.sourceSheetName,
  };
}

export function importSourcePayload(
  source: TabularImportSource,
  index: number,
  options: {
    titleColumnIndex?: number;
    dropPosition?: { x: number; y: number };
    sourceFileName?: string;
    sourceSheetName?: string;
  }
): DatabaseImportSourcePayload {
  return {
    ...importSourceMetadata(
      {
        name: source.name,
        columns: source.columns,
        rowCount: source.rows.length,
      },
      index,
      options
    ),
    rows: makeDatabaseImportRows(source.rows, source.name),
  };
}
